/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {
    ErrorType, IndexedObject, Link, nullify, ResponseData, showDialog, showSnackbar, stopPropagation
} from '@dvrd/dvr-controls';
import {defer} from 'lodash';
import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router';
import Account from '../../models/accountModel';
import EmailStep from '../../models/emailStepModel';
import EmailTemplate from '../../models/emailTemplateModel';
import {validateEmailSteps, validateStep} from '../../util/bolValidation';
import {EMAIL_TEMPLATE_ROUTE} from '../../util/constants';
import {onSetCrumbs} from '../../util/events';
import {ActivateHandle, EmailStatLoader, EmailStepStatTotal} from '../../util/interfaces';
import {setNavigationParam} from '../../util/navigateUtil';
import {PermissionCallback} from '../../util/pageChanger';
import {
    copyEmailSteps, getEmailStepStatsTotal, getGlobalEmailStats, getProductsCount, sendHistoryMails, updateAllEmailSteps
} from '../../util/requestSender';
import {createErrorMessage} from '../../util/utils';
import AccountSelectController from '../accountSelect/accountSelectController';
import {AppContext} from '../misc/appContext';
import PageChangePermission from '../misc/pageChangePermission';
import EmailStepFlow from './emailFlow';
import FlowSelectController from './flow/flowSelectController';
import EmailStepStatsController from './stats/emailStepStatsController';
import TemplateSelectController from './template/templateSelectController';

function setStepWarnings(steps: EmailStep[]): IndexedObject<ErrorType> {
    const stepWarnings: IndexedObject<ErrorType> = {};
    for (const step of steps)
        stepWarnings[step.id] = validateStep(step, steps);
    return stepWarnings;
}

const maxStatLoaders = 5;

export default function EmailFlowController() {
    const context = useContext(AppContext);
    const {account} = context.accountContext;
    const [steps, setSteps] = useState<EmailStep[]>([]);
    const [templates, setTemplates] = useState<EmailTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [warnChange, setWarnChange] = useState(false);
    const [templateNavigation, setTemplateNavigation] = useState(false);
    const [search, setSearch] = useState('');
    const [globalStats, setGlobalStats] = useState<EmailStepStatTotal>({SENT: -1, CLICK: -1, OPEN: -1});
    const [productsCount, setProductsCount] = useState(-1);
    const stepsLoaded = useRef(false);
    const templatesLoaded = useRef(false);
    const statsRef = useRef<ActivateHandle>(null);
    const flowSelectRef = useRef<ActivateHandle>(null);
    const templateRef = useRef<ActivateHandle>(null);
    const enabled = useMemo(() => steps.find((step: EmailStep) => step.active) !== undefined, [steps]);
    const copyRef = useRef<ActivateHandle>(null);
    const stepWarnings: IndexedObject<string | null> = useMemo(() => {
        return setStepWarnings(steps);
    }, [steps]);
    const firstError = Object.values(stepWarnings).find((error: string | null) => error !== null);
    const loadingStepStats = useRef<Array<EmailStatLoader>>([]);
    const loadingStatsCount = useRef<number>(0);
    const navigate = useNavigate();

    function onLoadStepStats(loader: EmailStatLoader) {
        if (!account) return;
        if (loadingStatsCount.current >= maxStatLoaders)
            return loadingStepStats.current.push(loader);
        loadStepStats(loader);
    }

    function onChangeSearch(value: string) {
        setSearch(value);
    }

    function onSearch() {
        loadSteps();
    }

    function onClickImport() {
        copyRef.current?.activate();
    }

    function onImport(accountId: string) {
        if (!accountId.length || !account) return;
        setLoading(true);
        copyEmailSteps({
            data: {
                account_id: account.id, from_account_id: accountId
            }, callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) {
                    account.emailSteps = data.email_steps.map((em: any) => new EmailStep(em));
                    context.onChangeContext({account});
                    showSnackbar('De e-mails zijn gekopieerd');
                    copyRef.current?.close?.();
                    loadData();
                } else
                    showDialog(createErrorMessage(data.message ?? 'Het kopiëren van de e-mails is niet gelukt.'),
                        'Kopiëren mislukt');
            }
        });
    }

    function onClickTemplate(step: EmailStep) {
        return function () {
            if (allowChange())
                templateRef.current?.activate(step, step.templateId !== '');
        };
    }

    function onSelectFlow(step: EmailStep) {
        step.deleteFlex('new');
        onReplaceStep(step);
        defer(onClickTemplate(step));
    }

    function onReplaceStep(step: EmailStep) {
        step = step.clone();
        let emailSteps = steps.slice();
        let found: boolean = false;
        for (let i = 0; i < emailSteps.length; i++) {
            if (emailSteps[i].id == step.id) {
                emailSteps.splice(i, 1, step);
                found = true;
                break;
            }
        }
        if (!found) emailSteps.push(step);
        setSteps(emailSteps);
    }

    function onDeleteStep(step: EmailStep) {
        setSteps(steps.filter((stp: EmailStep) => stp.id !== step.id));
    }

    function onOpenStats(step?: EmailStep) {
        return function () {
            statsRef.current?.activate(step);
        };
    }

    function onDenyPageChange(id: number | null, callback: PermissionCallback) {
        onChangeFlowActive(false, () => {
            callback(id, true);
        })(true);
    }

    function onChangeFlowActive(policyConfirmed: boolean = false, callback?: VoidFunction): any {
        return function (active: boolean) {
            const {account} = context.accountContext, {user} = context.userContext;
            if (!account || !user) return;
            const accountId = account.id;
            if (active && !validateSteps(steps)) return;
            else if (active && !policyConfirmed) return confirmPolicy(steps, callback);
            setLoading(true);
            updateSteps(active, accountId, callback);
        };
    }

    function onAddStep() {
        if (allowChange())
            flowSelectRef.current?.activate();
    }

    function onConfirmCloseSendHistoryMails(evt: React.MouseEvent) {
        stopPropagation(evt);
        showDialog(<p>
            Wij bieden hier de optie om ook <b>eenmalig</b> de e-mails te versturen naar orders van de afgelopen
            30 dagen.<br/><br/>Weet je zeker dat je hier geen gebruik van wilt maken?<br/>Dan zal deze knop verdwijnen.
            <br/><br/>Voor nieuwe orders kun je de e-mails laten inplannen door simpelweg rechtsboven de knop
            'Automatisch mailen' op AAN te zetten.<br/>
        </p>, 'Eenmalige mail optie sluiten', [
            {label: 'Nee', primary: false},
            {label: 'Ja', onClick: onCloseSendHistoryMails}
        ]);
    }

    function onCloseSendHistoryMails() {
        const {account} = context.accountContext;
        if (!account) return;
        account.setFlex('history_mails_denied', true);
        context.onChangeContext({account});
        account.update(undefined, undefined, undefined, (account: Account) => {
            context.onChangeContext({account});
        });
    }

    function onConfirmHistoryMails() {
        if (!!firstError) {
            return showDialog(<p>Je e-mails voldoen niet aan de beleidspunten van Bol. Er worden meerdere
                e-mails naar dezelfde klanten verzonden of de factuur is niet als bijlage ingesteld, volgens het beleid
                van Bol mag iedere klant maar één e-mail ontvangen en moet de factuur als bijlage worden
                toegevoegd.<br/><br/>De eenmalige e-mails kunnen alleen worden verzonden als er wordt voldaan aan de
                beleidspunten.<br/>Pas je e-mails aan zodat er maar één mail per klant wordt verstuurd.
            </p>, 'Beleidspunten');
        } else if (!enabled) {
            return showDialog(<p>Zet eerst het automatisch mailen AAN zodat de e-mails ook zal worden verstuurd
                naar nieuwe orders.</p>, 'Zet mailen aan');
        }
        showDialog(
            <div>
                <p>
                    Wij bieden hier de optie om <b>eenmalig</b> de e-mails te versturen naar orders van de
                    afgelopen 30 dagen.<br/><br/>Denk goed na of de inhoud van jouw e-mail klopt. Deze actie is eenmalig
                    uit te voeren en na uitvoeren verdwijnt deze knop voor altijd.<br/><br/>Je kunt de e-mails
                    laten inplannen voor <b>nieuwe orders</b> door simpelweg rechtsboven de knop 'Automatisch
                    mailen' op AAN te zetten.<br/>Dit heb je nu aan staan, dus deze mails zullen worden
                    ingepland.<br/><br/>Ik begrijp dat...<br/><br/>
                </p>
                <ul>
                    <li>Deze actie eenmalig de e-mails verstuurt voor orders van de afgelopen 30 dagen.</li>
                    <li>Deze actie na akkoord niet terug te draaien is.</li>
                    <li>Voor nieuwe orders de e-mails worden ingepland zoals ingesteld als rechtsboven de
                        automatische verzending van de e-mails op AAN staat.
                    </li>
                </ul>
            </div>, 'E-mails voor oude orders verzenden', [{label: 'Nee', primary: false}, {
                label: 'Ja, ik begrijp het & verzenden',
                onClick: onSendHistoryMails
            }]);
    }

    function onSendHistoryMails() {
        if (!enabled || !!firstError) return;
        const {account} = context.accountContext;
        if (!account) return;
        setLoading(true);
        sendHistoryMails({
            account_id: account.id, callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) {
                    context.onChangeContext({account: new Account(data.account)});
                    showDialog('De e-mails worden verzonden voor de orders van de afgelopen 30 dagen.',
                        'E-mails worden ingepland');
                } else showDialog(createErrorMessage(data.message ??
                        'Het verzenden van de e-mails voor de orders van de afgelopen 30 dagen is niet gelukt.'),
                    'Verzenden mislukt');
            }
        });
    }

    function loadStepStats(params: EmailStatLoader) {
        loadingStepStats.current = loadingStepStats.current.filter(
            (loader: EmailStatLoader) => loader.id !== params.id);
        if (!account) return;
        loadingStatsCount.current += 1;
        getEmailStepStatsTotal({
            email_step_id: params.id,
            account_id: account.id,
            callback: statsLoaderCallback(params.callback)
        });
    }

    function statsLoaderCallback(callback: (data: ResponseData) => void) {
        return function (data: ResponseData) {
            loadingStatsCount.current -= 1;
            callback(data);
            if (loadingStepStats.current.length) loadStepStats(loadingStepStats.current[0]);
        };
    }

    function updateSteps(active: boolean, accountId: string, callback?: VoidFunction) {
        setLoading(true);
        if (active)
            saveAccountSetupProgress();
        updateAllEmailSteps({
            data: {
                account_id: accountId,
                email_steps: steps.map((step: EmailStep) => ({email_step_id: step.id, active, account_id: accountId}))
            },
            callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) {
                    setSteps(data.email_steps.map((stp: any) => new EmailStep(stp)));
                    callback?.();
                } else showDialog(createErrorMessage(data.message ?? 'Het bijwerken van de e-mails is niet gelukt.'),
                    'Bijwerken mislukt');
            }
        });
    }

    function saveAccountSetupProgress() {
        const {account} = context.accountContext;
        if (!account || account.setupProgress.mail) return;
        account.setupProgress.mail = true;
        account.update(undefined, undefined, undefined,
            (account: Account, success: boolean) => {
                if (success) context.onChangeContext({account});
            });
    }

    function confirmPolicy(steps: EmailStep[], callback?: VoidFunction) {
        const matchesPolicy = validateEmailSteps(steps);
        if (matchesPolicy !== null) {
            const message = <label>Eén of meer stappen voldoen niet aan het <Link
                route='https://partnerplatform.bol.com/nl/hulp-nodig/prestaties/beleidspunten-zelfpromotie/'>beleid van
                Bol</Link>. Wil je de automatische verzending van de e-mails toch activeren?</label>;
            showDialog(message, 'Bol beleid', ['Nee', {
                label: 'Ja', primary: false, onClick: () => {
                    onChangeFlowActive(true, callback)(true);
                }
            }]);
        } else onChangeFlowActive(true, callback)(true);
    }

    function validateSteps(steps: EmailStep[]): boolean {
        let valid: boolean = true;
        if (!!firstError) {
            showDialog(<p>Je e-mails voldoen niet aan de beleidspunten van bol. Er worden meerdere
                e-mails naar dezelfde klanten verzonden of de factuur is niet als bijlage ingesteld, volgens het beleid
                van bol mag iedere klant maar één e-mail ontvangen en moet de factuur als bijlage worden
                toegevoegd.<br/><br/>Pas je e-mails aan zodat deze aan de beleidspunten van bol voldoen om de
                automatische verzending in te kunnen schakelen.
            </p>, 'Beleidspunten');
            valid = false;
        } else {
            if (!steps.length) {
                showDialog('Je hebt nog geen e-mail ingesteld.', 'Fout');
                return false;
            }
            for (const step of steps) {
                if (!step.templateId) {
                    valid = false;
                    break;
                }
                if (!step.productEans.length) {
                    valid = false;
                    break;
                }
            }
            if (!valid) showDialog(<label>Nog niet alle vereiste velden zijn ingesteld.<br/><br/>Zie blauw omlijnde
                veld(en).</label>, 'Fout');
        }
        return valid;
    }

    function loadSteps() {
        const {account} = context.accountContext;
        if (!account) {
            stepsLoaded.current = true;
            return finishLoading();
        }
        setLoading(true);
        stepsLoaded.current = false;
        EmailStep.getAll(account.id, nullify(search), (steps: EmailStep[], success: boolean, data: ResponseData) => {
            if (success) {
                setSteps(steps);
                loadTemplates();
            } else showDialog(createErrorMessage(data.message ?? 'Het laden van de e-mails is niet gelukt.'),
                'Laden mislukt');
            stepsLoaded.current = true;
            finishLoading();
            defer(scrollToNew);
        });
    }

    function loadGlobalStats() {
        if (!account) return;
        getGlobalEmailStats({
            account_id: account.id, callback: (data: ResponseData) => {
                if (!data.success) return;
                const {sent_count, open_count, click_count} = data;
                setGlobalStats({SENT: sent_count, OPEN: open_count, CLICK: click_count});
            }
        });
    }

    function loadProductsCount() {
        if (!account) return;
        getProductsCount({
            account_id: account.id, callback: (data: ResponseData) => {
                if (data.success) setProductsCount(data.count);
            }
        });
    }

    function loadTemplates() {
        const {account} = context.accountContext;
        if (!account) {
            templatesLoaded.current = true;
            return finishLoading();
        }
        templatesLoaded.current = false;
        EmailTemplate.getAll(account.id, -1, -1, (templates: EmailTemplate[], success: boolean, data: ResponseData) => {
            if (success) setTemplates(templates);
            else showDialog(createErrorMessage(data.message ?? 'Het laden van de e-mail templates is niet gelukt.'),
                'Laden mislukt');
            templatesLoaded.current = true;
            finishLoading();
        });
    }

    function loadData() {
        loadSteps();
        loadGlobalStats();
        loadProductsCount();
    }

    function finishLoading() {
        if (stepsLoaded.current && templatesLoaded.current) setLoading(false);
    }

    function allowChange(boolOnly: boolean = false) {
        if (context.userContext.user?.isAdmin()) return true;
        if (boolOnly) return !enabled;
        if (enabled) {
            setWarnChange(true);
            showDialog(<label>Pauzeer eerst <b>rechtsboven</b> het verzenden van mails om wijzingen door te
                voeren.</label>, 'Stap wijzigen');
            return false;
        }
        return true;
    }

    function scrollToNew() {
        const newElement = document.querySelector('div.email-step.new');
        if (!newElement) return;
        newElement.scrollIntoView({behavior: 'smooth'});
    }

    function onNavigateTemplate(template: EmailTemplate, step: EmailStep, returnPage?: string) {
        setTemplateNavigation(true);
        defer(() => {
            setNavigationParam({template, step, returnPage});
            navigate(EMAIL_TEMPLATE_ROUTE);
        });
    }

    useEffect(() => {
        onSetCrumbs([{label: 'E-mails'}]);
    }, []);

    useEffect(() => {
        loadData();
    }, [context.accountContext.account]);

    return (
        <>
            <EmailStepFlow onReplaceStep={onReplaceStep} onDeleteStep={onDeleteStep} onOpenStats={onOpenStats}
                           onChangeActive={onChangeFlowActive()} onClickTemplate={onClickTemplate}
                           onSendHistoryMails={onConfirmHistoryMails} onClickImport={onClickImport}
                           onCloseSendHistoryMails={onConfirmCloseSendHistoryMails} allowChange={allowChange}
                           loading={loading} steps={steps} templates={templates} flowIsActive={enabled}
                           warnChange={warnChange} onAddStep={onAddStep} onGlobalLoad={setLoading}
                           stepWarnings={stepWarnings} firstError={firstError} onNavigateTemplate={onNavigateTemplate}
                           search={search} onChangeSearch={onChangeSearch} onSearch={onSearch}
                           globalStats={globalStats} onLoadStepStats={onLoadStepStats} productsCount={productsCount}/>
            <EmailStepStatsController ref={statsRef}/>
            <FlowSelectController onReplaceStep={onSelectFlow} ref={flowSelectRef}/>
            <TemplateSelectController onReplaceStep={onReplaceStep} onNavigateTemplate={onNavigateTemplate}
                                      ref={templateRef}/>
            <AccountSelectController ref={copyRef} loading={loading} onImport={onImport} title='E-mails importeren'>
                <p className='import-text'>
                    Alle e-mails uit een ander account ophalen.
                    <br/><br/>
                    <b>Let op: </b>alle huidige emails worden overschreven!
                    <br/><br/>
                    Alleen bestaande EAN's worden meegenomen.
                </p>
            </AccountSelectController>
            <PageChangePermission changed={!templateNavigation && steps.length > 0 && !enabled}
                                  onDeny={onDenyPageChange}
                                  denyText='Ja, verzending inschakelen' allowText='Nee, pagina verlaten'
                                  allowSecondary={true} title='Automatische verzending e-mails'>
                De automatische verzending van de e-mails is niet ingeschakeld, er worden dus geen automatische e-mails
                verzonden. Wil je de automatische verzending van de e-mails inschakelen voordat je de pagina verlaat?
            </PageChangePermission>
        </>
    );
}